import React, { useState, useEffect } from 'react';
import { Upload, Link as LinkIcon, X } from 'lucide-react';
import LoadingSpinner from '@/Components/common/LoadingSpinner';
import MessageModal from '@/Components/common/MessageModal';
import useGroupProjectManagementStore from '@/stores/TrainerStore/GroupProjectManagementStore';
import {
  Project,
  createDeliverable,
  updateDeliverable,
  deleteDeliverable,
  getDeliverables
} from '@/services/TrainerServices/GroupProjectManagementService';
import './CreateProject.css';

interface Deliverable {
  id: string;
  title: string;
  isNew?: boolean;
}

interface Resource {
  id: string;
  type: 'file' | 'link';
  name: string;
  url: string;
  file?: File;
  isNew?: boolean;
}

interface ProjectFormProps {
  project?: Project | null;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const ProjectForm: React.FC<ProjectFormProps> = ({ project, onSuccess, onCancel }) => {
  const { createProject, updateProject, loading } = useGroupProjectManagementStore();
  const isEditMode = !!project;

  // Form states
  const [projectTitle, setProjectTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [instructions, setInstructions] = useState<string>('');
  const [gradingCriteria, setGradingCriteria] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [status, setStatus] = useState<'draft' | 'active' | 'completed' | 'archived'>('draft');
  const [startDate, setStartDate] = useState<string>('');
  const [deadline, setDeadline] = useState<string>('');
  const [deliverables, setDeliverables] = useState<Deliverable[]>([
    { id: '1', title: '', isNew: true }
  ]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [teamSize, setTeamSize] = useState<string>('4');
  const [maxTeams, setMaxTeams] = useState<string>('10');
  const [totalPoints, setTotalPoints] = useState<string>('100');

  // Resource input states
  const [showResourceInput, setShowResourceInput] = useState<boolean>(false);
  const [resourceType, setResourceType] = useState<'file' | 'link'>('link');
  const [resourceUrl, setResourceUrl] = useState<string>('');
  const [resourceFile, setResourceFile] = useState<File | null>(null);

  // Modal states
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalType, setModalType] = useState<'success' | 'error'>('success');
  const [modalTitle, setModalTitle] = useState<string>('');
  const [modalMessage, setModalMessage] = useState<string>('');

  // Populate form fields when editing
  useEffect(() => {
    if (project) {
      setProjectTitle(project.name || '');
      setDescription(project.description || '');
      setInstructions(project.instructions || '');
      setGradingCriteria(project.gradingCriteria || '');
      setStatus(project.status || 'draft');
      setStartDate(project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : '');
      setDeadline(project.deadline ? new Date(project.deadline).toISOString().split('T')[0] : '');
      setTeamSize(project.membersPerTeam?.toString() || '4');
      setMaxTeams(project.maxTeams?.toString() || '10');
      setTotalPoints(project.totalPoints?.toString() || '100');

      // Load deliverables if editing
      if (project.id) {
        loadDeliverables(project.id);
      }

      // Load resources if available
      if (project.resources) {
        try {
          const parsedResources = JSON.parse(project.resources);
          setResources(parsedResources.map((r: any) => ({ ...r, isNew: false })));
        } catch (e) {
          console.error('Failed to parse resources:', e);
        }
      }
    }
  }, [project]);

  const loadDeliverables = async (projectId: number) => {
    try {
      const response = await getDeliverables(projectId);
      if (response.success && response.data) {
        const loadedDeliverables = response.data.map(d => ({
          id: d.id.toString(),
          title: d.title,
          isNew: false
        }));
        setDeliverables(loadedDeliverables.length > 0 ? loadedDeliverables : [{ id: '1', title: '', isNew: true }]);
      }
    } catch (error) {
      console.error('Failed to load deliverables:', error);
    }
  };

  const addDeliverable = () => {
    const newId = Date.now().toString();
    setDeliverables([...deliverables, { id: newId, title: '', isNew: true }]);
  };

  const updateDeliverableField = (id: string, field: keyof Deliverable, value: any) => {
    setDeliverables(
      deliverables.map(d => (d.id === id ? { ...d, [field]: value } : d))
    );
  };

  const removeDeliverable = (id: string) => {
    const target = deliverables.find((d) => d.id === id);
    if (!target) return;
    // If this row maps to a saved deliverable, delete it from the backend
    // so it doesn't reappear on reload.
    if (!target.isNew) {
      const numericId = parseInt(target.id, 10);
      if (Number.isFinite(numericId)) {
        deleteDeliverable(numericId).catch((err) => {
          console.error('Failed to delete deliverable:', err);
        });
      }
    }
    setDeliverables(deliverables.filter((d) => d.id !== id));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setResourceFile(e.target.files[0]);
    }
  };

  const addResource = () => {
    if (resourceType === 'link') {
      if (!resourceUrl.trim()) {
        setModalType('error');
        setModalTitle('Validation Error');
        setModalMessage('Please provide a URL/link.');
        setModalOpen(true);
        return;
      }

      const newResource: Resource = {
        id: Date.now().toString(),
        type: 'link',
        name: resourceUrl,
        url: resourceUrl,
        isNew: true
      };

      setResources([...resources, newResource]);
      setResourceUrl('');
    } else {
      // File type
      if (!resourceFile) {
        setModalType('error');
        setModalTitle('Validation Error');
        setModalMessage('Please select a file.');
        setModalOpen(true);
        return;
      }

      // For now, we'll store file name as URL (in production, you'd upload to server first)
      const newResource: Resource = {
        id: Date.now().toString(),
        type: 'file',
        name: resourceFile.name,
        url: resourceFile.name, // In production, this would be the uploaded file URL
        file: resourceFile,
        isNew: true
      };

      setResources([...resources, newResource]);
      setResourceFile(null);
    }

    setShowResourceInput(false);
  };

  const removeResource = (id: string) => {
    setResources(resources.filter(r => r.id !== id));
  };

  const saveDeliverables = async (projectId: number) => {
    try {
      const deliverablesWithContent = deliverables.filter(d => d.title.trim());

      for (let i = 0; i < deliverablesWithContent.length; i++) {
        const deliverable = deliverablesWithContent[i];
        if (deliverable.isNew) {
          // Create new deliverable
          await createDeliverable(projectId, {
            title: deliverable.title,
            displayOrder: i
          });
        } else {
          // Update existing deliverable
          await updateDeliverable(parseInt(deliverable.id), {
            title: deliverable.title,
            displayOrder: i
          });
        }
      }
    } catch (error) {
      console.error('Error saving deliverables:', error);
      throw error;
    }
  };

  const handleSubmit = async (publishStatus: 'draft' | 'active') => {
    // The status dropdown wins when the user has explicitly chosen a
    // terminal state — Publish/Save Draft buttons only force draft/active
    // for the common path.
    const effectiveStatus: 'draft' | 'active' | 'completed' | 'archived' =
      status === 'completed' || status === 'archived' ? status : publishStatus;

    // Validation
    if (!projectTitle.trim()) {
      setModalType('error');
      setModalTitle('Validation Error');
      setModalMessage('Project title is required.');
      setModalOpen(true);
      return;
    }

    if (startDate && deadline && deadline < startDate) {
      setModalType('error');
      setModalTitle('Validation Error');
      setModalMessage('Deadline cannot be earlier than the start date.');
      setModalOpen(true);
      return;
    }

    if (effectiveStatus === 'active' && !deadline) {
      setModalType('error');
      setModalTitle('Validation Error');
      setModalMessage('Deadline is required to publish a project.');
      setModalOpen(true);
      return;
    }

    try {
      // Prepare resources as JSON string (without file objects)
      const resourcesJson = resources.length > 0
        ? JSON.stringify(resources.map(r => ({
            id: r.id,
            type: r.type,
            name: r.name,
            url: r.url
          })))
        : undefined;

      const projectData = {
        name: projectTitle,
        description: description || undefined,
        instructions: instructions || undefined,
        gradingCriteria: gradingCriteria || undefined,
        status: effectiveStatus,
        startDate: startDate || undefined,
        deadline: deadline || undefined,
        maxTeams: parseInt(maxTeams) || 10,
        membersPerTeam: parseInt(teamSize) || 4,
        totalPoints: parseInt(totalPoints) || 100,
        resources: resourcesJson,
      };

      // Call the store's createProject or updateProject action
      const response = isEditMode && project
        ? await updateProject(project.id, projectData)
        : await createProject(projectData);

      if (response.success && response.data) {
        // Save deliverables after project is created/updated
        try {
          await saveDeliverables(response.data.id);
        } catch (deliverableError) {
          console.error('Deliverables save error:', deliverableError);
          // Continue even if deliverables fail
        }

        // Success
        const statusLabel =
          effectiveStatus === 'active'
            ? 'Published'
            : effectiveStatus === 'completed'
            ? 'Completed'
            : effectiveStatus === 'archived'
            ? 'Archived'
            : 'Draft Saved';
        setModalType('success');
        setModalTitle(isEditMode ? 'Project Updated' : `Project ${statusLabel}`);
        setModalMessage(
          isEditMode
            ? 'Your group project has been successfully updated.'
            : effectiveStatus === 'active'
            ? 'Your group project has been successfully published and is now available to students.'
            : effectiveStatus === 'draft'
            ? 'Your project has been saved as a draft. You can continue editing it later.'
            : `Your project has been saved as ${effectiveStatus}.`
        );
        setModalOpen(true);

        // Call onSuccess callback or reset form
        setTimeout(() => {
          if (onSuccess) {
            onSuccess();
          } else {
            resetForm();
          }
        }, 1000);
      } else {
        // Error from API
        setModalType('error');
        setModalTitle(isEditMode ? 'Update Failed' : 'Save Failed');
        setModalMessage(
          response.error?.message ||
          `Failed to ${isEditMode ? 'update' : 'save'} the project. Please try again.`
        );
        setModalOpen(true);
      }
    } catch (error: any) {
      setModalType('error');
      setModalTitle(isEditMode ? 'Update Failed' : 'Save Failed');
      setModalMessage(
        error.message ||
        `Failed to ${isEditMode ? 'update' : 'save'} the project. Please try again.`
      );
      setModalOpen(true);
    }
  };

  const resetForm = () => {
    setProjectTitle('');
    setDescription('');
    setInstructions('');
    setGradingCriteria('');
    setCategory('');
    setStatus('draft');
    setStartDate('');
    setDeadline('');
    setDeliverables([{ id: '1', title: '', isNew: true }]);
    setResources([]);
    setTeamSize('4');
    setMaxTeams('10');
    setTotalPoints('100');
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? Unsaved changes will be lost.')) {
      if (onCancel) {
        onCancel();
      } else {
        resetForm();
      }
    }
  };

  return (
    <>
      {/* Loading Spinner Overlay */}
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <LoadingSpinner size="lg" message="Processing..." fullScreen={false} />
        </div>
      )}

      {/* Success/Error Modal */}
      <MessageModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        type={modalType}
        title={modalTitle}
        message={modalMessage}
      />

      <div className="trainer-group-create-project-container">
        <div className="trainer-group-create-project-main-content">
          {/* Project Information */}
          <div className="trainer-group-create-project-card">
            <div className="trainer-group-create-project-card-header">
              <svg className="trainer-group-create-project-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h2>Project Information</h2>
            </div>

            <div className="trainer-group-create-project-form-group">
              <label htmlFor="projectTitle" className="trainer-group-create-project-label">
                Project Title <span className="trainer-group-create-project-required">*</span>
              </label>
              <input
                id="projectTitle"
                type="text"
                className="trainer-group-create-project-input"
                placeholder="e.g., Customer Service Excellence Initiative"
                value={projectTitle}
                onChange={(e) => setProjectTitle(e.target.value)}
              />
            </div>

            <div className="trainer-group-create-project-form-group">
              <label htmlFor="description" className="trainer-group-create-project-label">Description</label>
              <textarea
                id="description"
                className="trainer-group-create-project-textarea"
                placeholder="Provide a brief description of the project..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>

            <div className="trainer-group-create-project-form-group">
              <label htmlFor="instructions" className="trainer-group-create-project-label">Instructions</label>
              <textarea
                id="instructions"
                className="trainer-group-create-project-textarea"
                placeholder="Provide detailed instructions for students..."
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                rows={4}
              />
            </div>

            <div className="trainer-group-create-project-form-group">
              <label htmlFor="gradingCriteria" className="trainer-group-create-project-label">Grading Criteria</label>
              <textarea
                id="gradingCriteria"
                className="trainer-group-create-project-textarea"
                placeholder="Describe how the project will be graded..."
                value={gradingCriteria}
                onChange={(e) => setGradingCriteria(e.target.value)}
                rows={3}
              />
            </div>

            <div className="trainer-group-create-project-form-row">
              <div className="trainer-group-create-project-form-group">
                <label htmlFor="status" className="trainer-group-create-project-label">Status</label>
                <select
                  id="status"
                  className="trainer-group-create-project-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as any)}
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  {isEditMode && <option value="completed">Completed</option>}
                  {isEditMode && <option value="archived">Archived</option>}
                </select>
              </div>

              <div className="trainer-group-create-project-form-group">
                <label htmlFor="totalPoints" className="trainer-group-create-project-label">Total Points</label>
                <input
                  id="totalPoints"
                  type="number"
                  className="trainer-group-create-project-input"
                  placeholder="100"
                  value={totalPoints}
                  onChange={(e) => setTotalPoints(e.target.value)}
                  min="0"
                />
              </div>
            </div>

            <div className="trainer-group-create-project-form-row">
              <div className="trainer-group-create-project-form-group">
                <label htmlFor="startDate" className="trainer-group-create-project-label">Start Date</label>
                <input
                  id="startDate"
                  type="date"
                  className="trainer-group-create-project-input"
                  value={startDate}
                  onChange={(e) => {
                    const next = e.target.value;
                    setStartDate(next);
                    // If start date moves past an existing deadline, clear
                    // the deadline so the user doesn't ship an inverted range.
                    if (next && deadline && deadline < next) {
                      setDeadline('');
                    }
                  }}
                />
              </div>

              <div className="trainer-group-create-project-form-group">
                <label htmlFor="deadline" className="trainer-group-create-project-label">
                  Deadline <span className="trainer-group-create-project-required">*</span>
                </label>
                <input
                  id="deadline"
                  type="date"
                  className="trainer-group-create-project-input"
                  value={deadline}
                  min={startDate || undefined}
                  onChange={(e) => setDeadline(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Resources */}
          <div className="trainer-group-create-project-card">
            <div className="trainer-group-create-project-card-header">
              <svg className="trainer-group-create-project-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              <h2>Project Resources</h2>
            </div>

            {!showResourceInput && (
              <button
                className="trainer-group-create-project-upload-button"
                onClick={() => setShowResourceInput(true)}
              >
                <svg className="trainer-group-create-project-icon-small" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Resource (File or Link)
              </button>
            )}

            {showResourceInput && (
              <div style={{ marginBottom: '1rem' }}>
                <div className="trainer-group-create-project-form-group">
                  <label className="trainer-group-create-project-label">Resource Type</label>
                  <select
                    className="trainer-group-create-project-select"
                    value={resourceType}
                    onChange={(e) => {
                      setResourceType(e.target.value as 'file' | 'link');
                      setResourceUrl('');
                      setResourceFile(null);
                    }}
                  >
                    <option value="link">Link/URL</option>
                    <option value="file">File</option>
                  </select>
                </div>

                {resourceType === 'link' ? (
                  <div className="trainer-group-create-project-form-group">
                    <label className="trainer-group-create-project-label">URL/Link</label>
                    <input
                      type="text"
                      className="trainer-group-create-project-input"
                      placeholder="https://..."
                      value={resourceUrl}
                      onChange={(e) => setResourceUrl(e.target.value)}
                    />
                  </div>
                ) : (
                  <div className="trainer-group-create-project-form-group">
                    <label className="trainer-group-create-project-label">Select File</label>
                    <input
                      type="file"
                      className="trainer-group-create-project-input"
                      onChange={handleFileSelect}
                      accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
                    />
                    {resourceFile && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                        Selected: {resourceFile.name}
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    className="trainer-group-create-project-btn trainer-group-create-project-btn-primary"
                    onClick={addResource}
                    style={{ flex: 1 }}
                  >
                    Add Resource
                  </button>
                  <button
                    className="trainer-group-create-project-btn trainer-group-create-project-btn-ghost"
                    onClick={() => {
                      setShowResourceInput(false);
                      setResourceUrl('');
                      setResourceFile(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {resources.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <p style={{ fontWeight: '500', marginBottom: '0.5rem' }}>Added Resources:</p>
                {resources.map((resource) => (
                  <div key={resource.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.75rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '6px',
                    marginBottom: '0.5rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {resource.type === 'link' ? <LinkIcon size={16} /> : <Upload size={16} />}
                      <div>
                        <div style={{ fontWeight: '500' }}>{resource.name}</div>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          {resource.type === 'link' ? 'Link' : 'File'}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => removeResource(resource.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#ef4444'
                      }}
                    >
                      <X size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Deliverables */}
          <div className="trainer-group-create-project-card">
            <div className="trainer-group-create-project-card-header">
              <svg className="trainer-group-create-project-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <h2>Define Deliverables</h2>
            </div>

            <div className="trainer-group-create-project-deliverables-list">
              {deliverables.map((deliverable, index) => (
                <div key={deliverable.id} style={{
                  marginBottom: '1rem',
                  padding: '1rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <label style={{ fontWeight: '500' }}>Deliverable {index + 1}</label>
                    <button
                      type="button"
                      onClick={() => removeDeliverable(deliverable.id)}
                      aria-label="Remove deliverable"
                      title="Remove this deliverable"
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#ef4444',
                      }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <input
                    type="text"
                    className="trainer-group-create-project-input"
                    placeholder="Deliverable title"
                    value={deliverable.title}
                    onChange={(e) => updateDeliverableField(deliverable.id, 'title', e.target.value)}
                  />
                </div>
              ))}
            </div>

            <button className="trainer-group-create-project-add-button" onClick={addDeliverable}>
              <span className="trainer-group-create-project-plus">+</span>
              Add Deliverable
            </button>
          </div>

          {/* Team Settings */}
          <div className="trainer-group-create-project-card">
            <div className="trainer-group-create-project-card-header">
              <svg className="trainer-group-create-project-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h2>Team Settings</h2>
            </div>

            <div className="trainer-group-create-project-form-row">
              <div className="trainer-group-create-project-form-group">
                <label htmlFor="maxTeams" className="trainer-group-create-project-label">Max Teams</label>
                <input
                  id="maxTeams"
                  type="number"
                  className="trainer-group-create-project-input"
                  value={maxTeams}
                  onChange={(e) => setMaxTeams(e.target.value)}
                  min="1"
                  max="100"
                />
              </div>

              <div className="trainer-group-create-project-form-group">
                <label htmlFor="teamSize" className="trainer-group-create-project-label">Members per Team</label>
                <input
                  id="teamSize"
                  type="number"
                  className="trainer-group-create-project-input"
                  value={teamSize}
                  onChange={(e) => setTeamSize(e.target.value)}
                  min="1"
                  max="20"
                />
              </div>
            </div>

            <div style={{ padding: '0.75rem', backgroundColor: '#f0f9ff', borderRadius: '6px', marginTop: '0.5rem' }}>
              <div style={{ fontSize: '0.875rem', color: '#0369a1' }}>
                <strong>Team Creation:</strong> Automatic System Assign (Teams will be created automatically based on the settings above)
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar Actions */}
        <div className="trainer-group-create-project-sidebar">
          <div className="trainer-group-create-project-actions-card">
            <h3 className="trainer-group-create-project-actions-title">Actions</h3>

            <button
              className="trainer-group-create-project-btn trainer-group-create-project-btn-primary"
              onClick={() => handleSubmit('active')}
            >
              <svg className="trainer-group-create-project-icon-small" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {isEditMode ? 'Update & Publish' : 'Publish Project'}
            </button>

            <button
              className="trainer-group-create-project-btn trainer-group-create-project-btn-secondary"
              onClick={() => handleSubmit('draft')}
            >
              <svg className="trainer-group-create-project-icon-small" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
              </svg>
              {isEditMode ? 'Update as Draft' : 'Save as Draft'}
            </button>

            <button className="trainer-group-create-project-btn trainer-group-create-project-btn-ghost" onClick={handleCancel}>
              <svg className="trainer-group-create-project-icon-small" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProjectForm;
